import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";

import { buildSupersets } from "../../../../utils/prefix-calculator";

const {
  DATE_FORMATS: { DATE_YEAR_FORMAT, DATE_DAY_FORMAT },
} = require("../../../../constants");

import MonthExercise from "./MonthExercise";
import { getUserToRequest } from "../../settings/manage-coaches/utils/manage-coaches-utils";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { useDraggable, useDroppable } from "@dnd-kit/react";

function MonthDay({
  date,
  handleSelectDate,
  selectedDates,
  scheduledSessions,
  handleRemoveMultiple,
  expanded,
}) {
  const { t } = useTranslation("dashboard");
  const {
    locals: {
      activeUser,
      query,
      withWeight,
      fromMetric,
      organizationOwner,
      activeUserToRequest,
    },
  } = useRillContext();

  const [renderMenuOptionsOpened, setRenderMenuOptionsOpened] = useState(false);

  const dayString = date.clone().format(DATE_DAY_FORMAT);
  const exercises = scheduledSessions[dayString];

  const { ref: refDroppable } = useDroppable({
    id: `drop-${dayString}`,
    disabled: !!exercises?.length,
    data: {
      id: dayString,
    },
  });
  const { ref: refDraggable, handleRef: handleRefDraggable } = useDraggable({
    id: `drag-${dayString}`,
    data: {
      id: dayString,
    },
  });

  const userToMeasure = getUserToRequest(activeUser, organizationOwner);
  const isInActiveMonth =
    date.clone().month() === moment(query.month, DATE_YEAR_FORMAT).month();
  const isToday = dayString === moment().format(DATE_DAY_FORMAT);
  const isPast = date.clone().isBefore(moment(), "day");
  const cycleDetail = _.get(_.find(exercises, "cycle"), "cycle", {});
  const supersets = buildSupersets(exercises);

  const session = { exercises, order: cycleDetail.programSession };

  const units = activeUser.units;
  const round = userToMeasure.round;

  const intensityMeasure =
    _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";

  const renderMenuOptions = () => {
    return (
      <div className="more">
        <input type="radio" name="more" id={`more-${dayString}`} />
        <div
          className="drag-icon cursor-grab"
          ref={handleRefDraggable}
          role="div"
        >
          <img src="/img/new-icons/drag-outline.svg" alt={t("calendar.monthDay.dragAlt")} />
        </div>

        <details role="list" open={renderMenuOptionsOpened}>
          <summary
            onClick={(e) => {
              e.preventDefault();
              setRenderMenuOptionsOpened(!renderMenuOptionsOpened);
            }}
            onBlur={(e) => {
              e.preventDefault();
              setRenderMenuOptionsOpened(false);
            }}
            aria-haspopup="listbox"
            className="p-0! h-auto!"
          >
            <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
          </summary>
          <ul
            role="listbox"
            className={renderMenuOptionsOpened ? "opened" : ""}
          >
            <li
              onMouseDown={() => {
                submit(`/actions/refresh?`, {
                  method: "GET",
                  body: {
                    view: "day",
                    date: date.format(DATE_DAY_FORMAT),
                  },
                });
              }}
            >
              <a>{t("calendar.monthDay.view")}</a>
            </li>
            <li
              onMouseDown={() => {
                submit(`/actions/refresh?`, {
                  method: "GET",
                  body: {
                    "modal[type]": "calendar-move-workout",
                    day: date.format(DATE_DAY_FORMAT),
                  },
                });
              }}
            >
              <a>{t("calendar.monthDay.move")}</a>
            </li>
            <li
              onMouseDown={() => {
                submit(`/actions/refresh?`, {
                  method: "GET",
                  body: {
                    "modal[type]": "calendar-shift-workout",
                    day: date.format(DATE_DAY_FORMAT),
                  },
                });
              }}
            >
              <a>{t("calendar.monthDay.shift")}</a>
            </li>
            <li
              onMouseDown={() => {
                submit(`/actions/refresh?`, {
                  method: "GET",
                  body: {
                    "modal[type]": "calendar-duplicate-workout",
                    day: date.format(DATE_DAY_FORMAT),
                  },
                });
              }}
            >
              <a>{t("calendar.monthDay.duplicate")}</a>
            </li>
            <li onMouseDown={(e) => handleRemoveMultiple(e, date)}>
              <a className="text-(--danger)!">{t("calendar.monthDay.remove")}</a>
            </li>
          </ul>
        </details>
      </div>
    );
  };

  const renderHeader = () => (
    <div className="day-header">
      <SvgIcon
        className="cursor-pointer"
        onClickFn={(e) => handleSelectDate(e, dayString)}
        src={
          selectedDates.includes(dayString)
            ? "/img/new-icons/checked-square.svg"
            : "/img/new-icons/unchecked-square.svg"
        }
        noFilterOnLight={selectedDates.includes(dayString)}
      />
      <div className="date-container">
        <div className="date-text">{date.format("D")}</div>
      </div>
    </div>
  );

  const renderExercises = () => (
    <div className={`exercises-frame${expanded ? " expanded" : ""}`}>
      <ul className={`exercises-list${expanded ? " expanded" : ""}`}>
        {_.times(session.exercises.length, (idx) => {
          const exercise = session.exercises[idx];
          if (!exercise) return null;
          return (
            <MonthExercise
              key={exercise._id}
              exercise={exercise}
              supersets={supersets}
              withWeight={withWeight}
              fromMetric={fromMetric}
              units={units}
              round={round}
              expanded={expanded}
              intensityMeasure={intensityMeasure}
              isRiR={isRiR}
              date={date.format(DATE_DAY_FORMAT)}
              showCopyButton={false}
            />
          );
        })}
      </ul>
    </div>
  );

  const renderFloatingHeader = () => (
    <div className="floating-header">
      <label
        className={`selector${
          selectedDates.includes(dayString) ? " selected" : ""
        }`}
        id={`select-${dayString}`}
        onClick={(e) => handleSelectDate(e, dayString)}
      />
      <div style={{ opacity: selectedDates.length < 2 ? 1 : 0, pointerEvents: selectedDates.length < 2 ? 'auto' : 'none' }}>
        {renderMenuOptions()}
      </div>
    </div>
  );

  return (
    <div
      className={`day ${!isInActiveMonth ? " alt-other-month" : ""}${
        isPast ? " alt-past" : isToday ? " alt-today" : ""
      }${expanded ? " expanded" : ""}`}
    >
      <div
        className={`day-margin${
          session.exercises ? " draggable-dropzone--occupied" : ""
        }${expanded ? " expanded" : ""}`}
        ref={refDroppable}
      >
        {renderHeader(Boolean(session.exercises))}
        {session.exercises && (
          <div className="day-draggable" ref={refDraggable}>
            {renderFloatingHeader()}
            {renderExercises()}
          </div>
        )}
        <a
          className="add"
          href={`/actions/refresh?modal[type]=calendar-add-exercise&day=${date.format(
            DATE_DAY_FORMAT
          )}`}
        >
          <SvgIcon
            className="h-11 w-11"
            src="/img/new-icons/add-square-outline.svg"
            alt={t("calendar.monthDay.addWorkoutAlt")}
          />
          <p className="p4">{t("calendar.monthDay.addWorkout")}</p>
        </a>
      </div>
    </div>
  );
}

export default MonthDay;
